'use client'

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import type { ProcessedTheme, ProcessedEntry, DomainDetail, BrowserRow } from './processedTypes'
import { watchClasses, timelineDateLabel, postTypeLabel } from './processedTypes'
import { Widget } from '../../Widget'
import { parseDbTimestamp } from '@/lib/dates'

// ─── Browser campaign state helpers ──────────────────────────────────────────

export type BrowserCampaignState = 'up' | 'down' | 'mixed' | 'unknown'

function browserCampaignState(row: BrowserRow): BrowserCampaignState {
  if (row.mobileIsAlive === true && row.desktopIsAlive === true) return 'up'
  if (row.mobileIsAlive === false && row.desktopIsAlive === false) return 'down'
  if (row.mobileIsAlive === true || row.desktopIsAlive === true || row.mobileIsAlive === false || row.desktopIsAlive === false) return 'mixed'
  return 'unknown'
}

// border/bg/text used to be hardcoded dark-mode values (translucent dark
// bg + bright text) regardless of theme — fine on a dark page, but on a
// light one a 40%-opacity near-black bg composites to a mid-gray that
// swallows same-toned text (e.g. the numbered-node badges in
// TimelineDiagram), making the number unreadable. dot stays a flat
// saturated color either way since that already has good contrast on both.
export function browserStateMeta(state: BrowserCampaignState, theme: ProcessedTheme = 'dark') {
  const isDark = theme === 'dark'
  if (state === 'up')    return { label: 'UP',      dot: 'bg-success', text: 'text-success', border: 'border-success/40', bg: 'bg-success-surface' }
  if (state === 'down')  return { label: 'DOWN',    dot: 'bg-destructive',     text: 'text-destructive',     border: 'border-destructive/40',     bg: 'bg-danger-surface'     }
  if (state === 'mixed') return { label: 'MIXED',   dot: 'bg-warning',   text: 'text-warning',   border: 'border-warning-border',   bg: 'bg-warning-surface'   }
  return                        { label: 'UNKNOWN', dot: 'bg-subtle',   text: 'text-muted-foreground',   border: 'border-border',   bg: 'bg-secondary'   }
}

export function parseTimelineDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value.trim()) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

// ─── Connected step diagram ───────────────────────────────────────────────────

export type TimelineSummaryItem = {
  id: string
  label: string
  value: string
  date: Date
  state: BrowserCampaignState
  detail: string
  // date is still needed for chart positioning/sort order (min/max span,
  // left:% on the axis strip), but re-deriving a display string from it via
  // timelineDateLabel means trusting whatever `new Date(rawString)` decided
  // — fine for our own ISO timestamps (report/capture dates), not fine for
  // domain create_date, which is LLM-extracted from WHOIS text with no
  // enforced format: a plain "09/07/2026" is genuinely ambiguous (DD/MM vs
  // MM/DD), and re-parsing+reformatting it can silently show the wrong
  // date. rawLabel, when set, is shown verbatim instead of reformatting.
  rawLabel?: string
}

type Point = { x: number; y: number }

// Node circles are h-6 w-6 (24px, 12px radius) plus a hair of clearance so
// the stroke doesn't touch the border, and so the arrowhead marker (drawn
// at the path's endpoint) actually lands next to the circle instead of
// vanishing underneath it — a line run straight to the center would be
// hidden behind the node entirely.
const NODE_RADIUS = 14

function shrinkPoint(p: Point, towardDx: number, towardDy: number, r: number): Point {
  const len = Math.hypot(towardDx, towardDy) || 1
  return { x: p.x + (towardDx / len) * r, y: p.y + (towardDy / len) * r }
}

// Straight segment for two nodes on the same visual row; an elbow (down,
// across, down) when the next node has wrapped onto a new row — a plain
// diagonal there would cut across unrelated nodes in between and read as
// noise rather than "step 3 leads to step 4". Both endpoints are pulled
// back by the node radius so the line (and its arrowhead) starts and ends
// right at each circle's edge rather than running under it.
function connectorPath(a: Point, b: Point): string {
  if (Math.abs(a.y - b.y) < 16) {
    const start = shrinkPoint(a, b.x - a.x, b.y - a.y, NODE_RADIUS)
    const end = shrinkPoint(b, a.x - b.x, a.y - b.y, NODE_RADIUS)
    return `M ${start.x} ${start.y} L ${end.x} ${end.y}`
  }
  const midY = (a.y + b.y) / 2
  const start = shrinkPoint(a, 0, midY - a.y, NODE_RADIUS)
  const end = shrinkPoint(b, 0, midY - b.y, NODE_RADIUS)
  return `M ${start.x} ${start.y} L ${start.x} ${midY} L ${end.x} ${midY} L ${end.x} ${end.y}`
}

// Renders items in plain DOM order (1, 2, 3 on row one; 4, 5, 6 on row two —
// CSS grid's default row-major auto-flow) with a numbered node per step, and
// draws an SVG overlay connecting node N to node N+1 in sequence, including
// across a row wrap, so the begin→end order reads as one continuous path
// instead of the grid's column order.
export function TimelineDiagram({ items, theme }: { items: TimelineSummaryItem[]; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const containerRef = useRef<HTMLDivElement>(null)
  const nodeRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const [lines, setLines] = useState<{ id: string; d: string }[]>([])

  const measure = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const containerRect = container.getBoundingClientRect()
    const centers = items.map((item) => {
      const el = nodeRefs.current.get(item.id)
      if (!el) return null
      const r = el.getBoundingClientRect()
      return { x: r.left + r.width / 2 - containerRect.left, y: r.top + r.height / 2 - containerRect.top }
    })
    const next: { id: string; d: string }[] = []
    for (let i = 0; i < centers.length - 1; i++) {
      const a = centers[i]
      const b = centers[i + 1]
      if (a && b) next.push({ id: `${items[i].id}-${items[i + 1].id}`, d: connectorPath(a, b) })
    }
    setLines(next)
  }, [items])

  useLayoutEffect(() => {
    measure()
    const container = containerRef.current
    if (!container) return
    const ro = new ResizeObserver(measure)
    ro.observe(container)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  const lineColor = theme === 'dark' ? '#334155' : '#cbd5e1'

  // @container, not a viewport breakpoint: this widget gets embedded at very
  // different widths depending on context (a full-width card, one of three
  // columns in ProcessedEntryCard's grid, one of three in CttEntryDetailPage's
  // grid...) — sizing the column count off the browser viewport picks 3
  // columns whenever the viewport is wide even though THIS widget's own
  // rendered width might only be a few hundred px, cramming everything
  // together. Container queries key off the widget's actual width instead.
  return (
    <div className="@container">
    <div ref={containerRef} className="relative grid grid-cols-1 gap-x-10 gap-y-8 @md:grid-cols-2 @2xl:grid-cols-3">
      <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" aria-hidden>
        <defs>
          <marker id={`timeline-arrow-${theme}`} viewBox="0 0 8 8" refX="6.5" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0 0 L8 4 L0 8 Z" fill={lineColor} />
          </marker>
        </defs>
        {lines.map((line) => (
          <path key={line.id} d={line.d} fill="none" stroke={lineColor} strokeWidth={1.5} markerEnd={`url(#timeline-arrow-${theme})`} />
        ))}
      </svg>
      {items.map((item, index) => {
        const meta = browserStateMeta(item.state, theme)
        return (
          <div key={item.id} className="relative flex gap-3">
            <div
              ref={(el) => { if (el) nodeRefs.current.set(item.id, el); else nodeRefs.current.delete(item.id) }}
              className={`z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 font-mono text-[10px] font-bold ${meta.border} ${meta.bg} ${meta.text}`}
            >
              {index + 1}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`font-mono text-[10px] uppercase tracking-widest ${meta.text}`}>{item.label}</span>
                <span className={`font-mono text-[11px] ${c.faint}`}>{item.rawLabel ?? timelineDateLabel(item.date)}</span>
              </div>
              <div className={`mt-1 font-mono text-lg font-semibold leading-snug ${meta.text}`}>{item.value}</div>
              <div className={`mt-0.5 text-xs leading-relaxed ${c.muted}`}>{item.detail}</div>
            </div>
          </div>
        )
      })}
    </div>
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ProcessedEntryTimeline({
  entry,
  domain,
  browserData,
  theme,
}: {
  entry: ProcessedEntry
  domain: DomainDetail | null
  browserData: BrowserRow[]
  theme: ProcessedTheme
}) {
  const c = watchClasses(theme)
  // create_date is LLM-extracted from WHOIS text with no enforced format
  // (see the schema — it's declared as a free-form string) — domainDate is
  // still parsed for chart positioning (min/max span, axis placement, sort
  // order), but domainDateRaw (the untouched source string) is what's
  // actually shown, since re-parsing+reformatting an ambiguous date string
  // can silently swap day and month.
  const domainDateRaw = domain?.domainMetadata?.create_date ?? domain?.domainInfo?.created ?? domain?.createdAt ?? null
  const domainDate = parseTimelineDate(domainDateRaw)
  // entry.createdAt and row.createdAt (unlike domainDateRaw above) are real
  // DB timestamps -- parseDbTimestamp, not parseTimelineDate's tolerant
  // native parse, so they get the missing-'Z' UTC fix (see parseDbTimestamp).
  const reportDate = parseDbTimestamp(entry.createdAt)
  const domainMeta = domain?.domainMetadata ?? {}
  const domainInfo = domain?.domainInfo ?? {}
  const domainIp = domainMeta.ip_address ?? domainInfo.ip
  const domainAsn = domainMeta.asn ?? domainInfo.asn
  const domainRegistrar = domainInfo.registrar ?? domainMeta.registrar
  const rows = [...browserData]
    .map((row) => ({ row, date: parseDbTimestamp(row.createdAt) }))
    .filter((item): item is { row: BrowserRow; date: Date } => item.date !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime())

  // No entry_browser_data rows yet (domain not captured/probed) — nothing to
  // plot a "campaign status" progression from. Render an explicit empty
  // state rather than vanishing: with the widget just gone, its slot in the
  // Domain intelligence grid silently disappears, which reads as a bug
  // rather than "no captures yet, check back later".
  if (rows.length === 0) {
    return (
      <Widget label="Campaign status timeline" theme={theme} className="mb-5">
        <div className={`py-6 text-center font-mono text-[11px] uppercase tracking-widest ${c.faint}`}>
          No browser captures yet
        </div>
      </Widget>
    )
  }

  const first = rows[0]
  const last = rows[rows.length - 1]
  const firstState = browserCampaignState(first.row)
  const latestState = browserCampaignState(last.row)
  const stateChanges = rows.filter(({ row }, index) =>
    index === 0 || browserCampaignState(row) !== browserCampaignState(rows[index - 1].row),
  )
  const firstUp   = rows.find(({ row }) => browserCampaignState(row) === 'up' || browserCampaignState(row) === 'mixed')
  const firstDown = rows.find(({ row }) => browserCampaignState(row) === 'down')

  const allDates = [domainDate, reportDate, ...stateChanges.map(({ date }) => date)].filter((d): d is Date => d != null)
  const minTs = Math.min(...allDates.map((d) => d.getTime()))
  const maxTs = Math.max(...allDates.map((d) => d.getTime()))
  const span = Math.max(maxTs - minTs, 1)

  const timelineEvents = [
    domainDate ? {
      id: 'domain',
      date: domainDate,
      rawLabel: domainDateRaw ?? undefined,
      color: 'bg-signal',
      label: 'Domain',
      detail: [domainRegistrar ?? 'Registrar unavailable', domainIp ? `IP: ${domainIp}` : null, domainAsn ? `ASN: ${domainAsn}` : null].filter(Boolean).join(' · '),
    } : null,
    reportDate ? {
      id: 'report',
      date: reportDate,
      color: 'bg-warning',
      label: 'Report',
      detail: [entry.postType ? postTypeLabel(entry.postType) : null, entry.sender].filter(Boolean).join(' · ') || 'Processed entry created',
    } : null,
    ...stateChanges.map(({ row, date }, index) => {
      const meta = browserStateMeta(browserCampaignState(row))
      return {
        id: row.id,
        date,
        color: meta.dot,
        label: 'Capture',
        detail: `Mobile ${row.mobileIsAlive === true ? 'up' : row.mobileIsAlive === false ? 'down' : 'unknown'} · Desktop ${row.desktopIsAlive === true ? 'up' : row.desktopIsAlive === false ? 'down' : 'unknown'}${row.r2Key ? ' · Image available' : ''}`,
        title: index === 0 ? 'First browser capture' : `Browser ${meta.label}`,
      }
    }),
  ].filter((e): e is { id: string; date: Date; rawLabel?: string; color: string; label: string; detail: string; title?: string } => e != null)
    .sort((a, b) => a.date.getTime() - b.date.getTime())

  const contextEvents = [
    domainDate  ? `domain ${domainDateRaw}`   : null,
    reportDate  ? `report ${timelineDateLabel(reportDate)}`   : null,
  ].filter(Boolean)

  const latestMeta = browserStateMeta(latestState, theme)

  const browserDetail = (row: BrowserRow) =>
    `Mobile ${row.mobileIsAlive === true ? 'up' : row.mobileIsAlive === false ? 'down' : 'unknown'} · Desktop ${row.desktopIsAlive === true ? 'up' : row.desktopIsAlive === false ? 'down' : 'unknown'}${row.r2Key ? ' · Image available' : ''}`

  const summaryItems = [
    domainDate ? {
      id: 'domain', label: 'Domain', value: 'Domain record', date: domainDate, rawLabel: domainDateRaw ?? undefined, state: 'unknown' as BrowserCampaignState,
      detail: [domainRegistrar ? `Registrar: ${domainRegistrar}` : null, domainIp ? `IP: ${domainIp}` : null, domainAsn ? `ASN: ${domainAsn}` : null].filter(Boolean).join(' · ') || 'Domain enrichment loaded',
    } : null,
    reportDate ? {
      id: 'report', label: 'First report', value: 'REPORT', date: reportDate, state: 'mixed' as BrowserCampaignState,
      detail: [entry.postType ? postTypeLabel(entry.postType) : null, entry.sender].filter(Boolean).join(' · ') || 'Processed entry created',
    } : null,
    { id: 'first-seen', label: 'First seen', value: browserStateMeta(firstState).label, date: first.date, state: firstState, detail: browserDetail(first.row) },
    firstUp ? {
      id: 'first-up', label: 'First up', value: browserStateMeta(browserCampaignState(firstUp.row)).label,
      date: firstUp.date, state: browserCampaignState(firstUp.row), detail: browserDetail(firstUp.row),
    } : null,
    firstDown ? {
      id: 'first-down', label: 'First down', value: 'DOWN', date: firstDown.date, state: 'down' as BrowserCampaignState, detail: browserDetail(firstDown.row),
    } : null,
    { id: 'latest', label: 'Latest', value: latestMeta.label, date: last.date, state: latestState, detail: browserDetail(last.row) },
  ].filter((item): item is { id: string; label: string; value: string; date: Date; state: BrowserCampaignState; detail: string } => item != null)
    .sort((a, b) => a.date.getTime() - b.date.getTime())

  const dateRange = `${timelineDateLabel(new Date(minTs))} → ${timelineDateLabel(new Date(maxTs))}`
  const headerBadges = (
    <div className="flex flex-wrap gap-2">
      <span className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${latestMeta.border} ${latestMeta.bg} ${latestMeta.text}`}>
        Now {latestMeta.label}
      </span>
      <span className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${c.line} ${c.soft} ${c.muted}`}>
        {rows.length} captures
      </span>
      <span className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${c.line} ${c.soft} ${c.muted}`}>
        {stateChanges.length} state changes
      </span>
    </div>
  )

  return (
    <Widget
      label={<>Campaign status timeline <span className={`ml-3 tracking-normal font-normal ${c.muted}`}>{dateRange}</span></>}
      theme={theme}
      headerRight={headerBadges}
      className="mb-5"
    >
      {contextEvents.length > 0 && (
        <div className={`mb-4 font-mono text-[10px] ${c.faint}`}>{contextEvents.join(' · ')}</div>
      )}

      <TimelineDiagram items={summaryItems} theme={theme} />

      <div className="mt-5 px-2">
        <div className="relative h-12">
          <div className={`absolute left-0 right-0 top-5 h-px bg-muted`} />
          {timelineEvents.map((event) => {
            const left = Math.min(98, Math.max(2, ((event.date.getTime() - minTs) / span) * 100))
            return (
              <div
                key={event.id}
                className="absolute top-2 -translate-x-1/2"
                style={{ left: `${left}%` }}
                title={`${event.label} · ${event.rawLabel ?? timelineDateLabel(event.date)}`}
              >
                <span className={`block h-6 w-6 rounded-full border-4 ${theme === 'dark' ? 'border-[#0f161c]' : 'border-white'} ${event.color} shadow-[0_0_0_1px_rgba(148,163,184,0.35)]`} />
              </div>
            )
          })}
        </div>
        <div className={`mt-1 flex justify-between font-mono text-[10px] ${c.faint}`}>
          <span>{timelineDateLabel(new Date(minTs))}</span>
          <span>{timelineDateLabel(new Date(maxTs))}</span>
        </div>
      </div>
    </Widget>
  )
}
